import { SsoType } from '@diritto/common/account/constants/sso.constant';
import { UserStatus } from '@diritto/common/account/constants/user.constant';
import { ProfileDto } from '@diritto/common/account/dtos/profile.dto';
import { SignUpResponseDto } from '@diritto/common/account/dtos/sign-up-response.dto';
import { UserMarketingAgreementDto } from '@diritto/common/account/dtos/user-marketing-agreement.dto';
import BaseTrackingService from '@diritto/common/tracker/BaseTrackingService';
import {
  TrackingEventParams,
  TrackingEventType,
  TrackingViewerSettingParams,
} from '@diritto/common/tracker/tracking-event.constant';
import { UserProperties } from '@diritto/common/tracker/user-properties.constant';
import { ViewerStyle } from '@diritto/common/viewer/constants/viewer.constant';
import FirebaseTracker from 'trackers/FirebaseTracker';
import GtmTracker from 'trackers/GtmTracker';
import MixpanelTracker from 'trackers/MixpanelTracker';
import { SentryService } from 'utils/SentryService';

// note: 수정시 AppTrackingService 도 고려해야함
export default class WebTrackingService implements BaseTrackingService {
  constructor(
    private readonly gtmTracker: GtmTracker,
    private readonly mixpanelTracker: MixpanelTracker,
    private readonly firebaseTracker: FirebaseTracker,
  ) {}

  trackSignIn(params: { userId: number; ssoType: SsoType }) {
    try {
      this.gtmTracker.setUser(params.userId);

      this.mixpanelTracker.identify(params.userId);
      this.mixpanelTracker.track(TrackingEventType.SignIn);
      this.firebaseTracker.logLogin(params.userId, params.ssoType);
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackSignOut() {
    try {
      this.gtmTracker.setUser();

      this.mixpanelTracker.track(TrackingEventType.SignOut);
      this.mixpanelTracker.reset();
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackSignUp(signUpSsoResponseDto: SignUpResponseDto) {
    const { userId, isAdult, mktAgreement, ssoType } = signUpSsoResponseDto;

    try {
      this.mixpanelTracker.alias(userId);
      this.mixpanelTracker.setPeople({
        [UserProperties.UserId]: userId,
        [UserProperties.IsAdult]: isAdult,
        [UserProperties.MktAgreement]: mktAgreement,
        [UserProperties.SsoType]: ssoType,
        [UserProperties.UserStatus]: UserStatus.Normal,
      });
      this.mixpanelTracker.track(TrackingEventType.SignUp, { ssoType });
      this.firebaseTracker.logSignUp(userId, ssoType);
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackWithdrawal() {
    try {
      this.mixpanelTracker.track(TrackingEventType.Withdrawal);
      this.mixpanelTracker.setPeople({ [UserProperties.UserStatus]: UserStatus.Withdrawal });
      this.mixpanelTracker.reset();
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackMktAgreement(userMktAgreementDto: UserMarketingAgreementDto) {
    const { agree, agreeNight } = userMktAgreementDto;

    try {
      this.mixpanelTracker.track(TrackingEventType.ClickMarketingAgreement, userMktAgreementDto);
      this.mixpanelTracker.setPeople({
        [UserProperties.MktAgreement]: agree,
        [UserProperties.MktAgreementNight]: agreeNight,
      });
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackCreateNewProfile(profileDto: ProfileDto) {
    try {
      this.mixpanelTracker.track(TrackingEventType.CreateNewProfile, {
        profileId: profileDto.id,
        profileNickname: profileDto.nickname,
        profileDescription: profileDto.description,
        profileEmail: profileDto.email,
        profileTwitter: profileDto.twitter,
      });

      this.mixpanelTracker.appendPeople(UserProperties.ProfileIds, profileDto.id);
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackChangeViewerSetting(property: { [x: string]: TrackingViewerSettingParams }) {
    try {
      this.mixpanelTracker.track(TrackingEventType.ChangeViewerSetting, property);
      this.mixpanelTracker.setPeople(property);
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  trackViewerSetting(property: ViewerStyle) {
    try {
      this.mixpanelTracker.setPeople(property);
    } catch (e) {
      SentryService.captureException(e);
    }
  }

  track<T extends TrackingEventType>(name: T, params: TrackingEventParams[T]) {
    setTimeout(() => {
      this.mixpanelTracker.track(name, params);
    }, 0);
    setTimeout(() => {
      this.firebaseTracker.track(name, params);
    }, 0);
  }
}
